import { real_id } from 'src/helpers/interface_helpers';
import { readDeviceStatus, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
import { btHomeRemoteIcon } from 'src/stores/features/ble/bt-home-remote-icon';
import { get } from 'svelte/store';
import { compare } from 'compare-versions';
import type { Device } from 'src/types/Device';
import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';
import { isFwAfter200 } from 'src/helpers/ble_devices_helpers';

const countMappings = (config: Record<string, any>): number => {
	if (!config || typeof config !== 'object' || Array.isArray(config)) {
		return 0;
	}

	let total = 0;

	Object.entries(config).forEach(([key, channelEvents]) => {
		if (!/^\d+:\d+$/.test(key)) {
			return;
		}

		if (!channelEvents || typeof channelEvents !== 'object' || Array.isArray(channelEvents)) {
			return;
		}

		const eventCount = Object.entries(channelEvents).filter(([, eventCfg]) => {
			return (
				eventCfg &&
				typeof eventCfg === 'object' &&
				!Array.isArray(eventCfg) &&
				typeof (eventCfg as Record<string, any>)?.action === 'string' &&
				(eventCfg as Record<string, any>).action
			);
		}).length;

		total += eventCount;
	});

	return total;
};

export const hasMappedRemoteControls = (
	device: Device,
	statusOverride?: Record<string, any>,
	settingsOverride?: DeviceSettingsG2 | Record<string, any>,
): boolean => {
	if (!device) {
		return false;
	}

	const deviceID = real_id(device.id);
	const remoteIconState = get(btHomeRemoteIcon)?.[deviceID];
	if (typeof remoteIconState?.active === 'boolean') {
		return remoteIconState.active;
	}

	const statusMap =
		statusOverride || ((readDeviceStatus(deviceID) as Record<string, any>) ?? undefined);

	const settings =
		(settingsOverride as DeviceSettingsG2 | undefined) ||
		(readDeviceSettings(deviceID) as DeviceSettingsG2 | undefined);

	if (!settings) {
		return false;
	}

	const settingsMap = settings as Record<string, any>;

	const fwVersion = (
		(settings as Record<string, any>)?.DeviceInfo?.ver ||
		settings?.sys?.device?.fw_id ||
		(settings as Record<string, any>)?.DeviceInfo?.fw_id
	)?.match(/(\d+\.)+\d+/)?.[0];

	const supportsBTHomeRemoteControls = fwVersion ? compare(fwVersion, '1.8.0', '>=') : false;
	if (!supportsBTHomeRemoteControls) {
		return false;
	}

	const bleEnabled = isFwAfter200(settings) ? true : settings?.ble?.enable;
	if (!bleEnabled) {
		return false;
	}

	const controlByAddr = new Map<string, string>();
	Object.entries(settingsMap).forEach(([key, config]) => {
		if (/^bthomecontrol:2\d{2}$/.test(key) && config && (config as Record<string, any>)?.addr) {
			controlByAddr.set((config as Record<string, any>).addr, key);
		}
	});

	let hasMappings = false;
	let statusHasRemoteEntries = false;

	if (statusMap) {
		statusHasRemoteEntries = Object.keys(statusMap).some((key) =>
			/^(bthomedevice|bthomecontrol):2\d{2}$/.test(key),
		);
	}

	const bthomeControlRegex = /^bthomecontrol:2\d{2}$/;
	const bthomeDeviceRegex = /^bthomedevice:2\d{2}$/;
	const bthomeSensorRegex = /^bthomesensor:2\d{2}$/;

	for (const [key, config] of Object.entries(settingsMap)) {
		if (!config || typeof config !== 'object' || config === null || Array.isArray(config)) {
			continue;
		}

		if (bthomeControlRegex.test(key)) {
			const controlConfig = config as Record<string, any>;
			const mappingCount = countMappings(controlConfig);
			if (mappingCount > 0) {
				hasMappings = true;
			}
		} else if (bthomeDeviceRegex.test(key)) {
			const deviceConfig = config as Record<string, any>;
			const addr = deviceConfig?.addr;
			let configToCount = deviceConfig;

			if (addr) {
				const controlKey = controlByAddr.get(addr);
				const controlConfig = controlKey
					? (settingsMap[controlKey] as Record<string, any>)
					: null;
				if (
					controlConfig &&
					typeof controlConfig === 'object' &&
					!Array.isArray(controlConfig)
				) {
					configToCount = controlConfig;
				}
			}

			const mappingCount = countMappings(configToCount);
			if (mappingCount > 0) {
				hasMappings = true;
			}
		}
	}

	const finalResult = hasMappings || statusHasRemoteEntries;

	btHomeRemoteIcon.setState(deviceID, finalResult);

	return finalResult;
};
